import React from 'react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
    return (
        <div className="relative flex min-h-screen w-full flex-col bg-white overflow-x-hidden font-display text-slate-900">
            {/* Navigation Bar */}
            <nav className="sticky top-0 z-50 bg-offwhite/80 backdrop-blur-md border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-20 items-center">
                        <Link to="/" className="flex items-center gap-2">
                            <span className="material-icons text-tangerine text-3xl">change_history</span>
                            <span className="font-bold text-xl tracking-tight text-black">Ally AI</span>
                        </Link>
                        <div className="hidden md:flex space-x-8 items-center">
                            <Link className="text-sm font-semibold text-gray-500 hover:text-tangerine transition-colors" to="/experts">Experts</Link>
                            <Link className="text-sm font-semibold text-gray-500 hover:text-tangerine transition-colors" to="/about">About</Link>
                            <a className="text-sm font-semibold text-gray-500 hover:text-tangerine transition-colors" href="#">Results</a>
                            <a className="text-sm font-semibold text-gray-500 hover:text-tangerine transition-colors" href="#"><br /></a>
                        </div>
                        <div className="flex items-center space-x-4">
                            <Link to="/login" className="text-sm font-semibold text-gray-500 hover:text-tangerine transition-colors hidden md:block">
                                Login
                            </Link>
                            <Link to="/register" className="bg-tangerine hover:bg-tangerine/90 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-md shadow-tangerine/20 transition-all hover:-translate-y-0.5">
                                Sign Up
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            <main className="flex-1 px-6 py-12 md:py-24 max-w-4xl mx-auto w-full">
                {/* Hero Section */}
                <section className="mb-24 md:mb-32">
                    <div className="inline-block px-3 py-1 mb-6 text-xs font-bold uppercase tracking-[0.2em] border border-tangerine/30 text-tangerine rounded-lg bg-tangerine/10">
                        Our Mission
                    </div>
                    <h1 className="text-5xl md:text-7xl font-bold leading-[1.1] mb-8 text-black text-balance">
                        Provide Access to Human Expertise.
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-500 font-light max-w-2xl leading-relaxed">
                        Ally is the intelligence infrastructure that translates real human expertise into evolving AI systems. We ensure knowledge is no longer limited by time, geography, or human capacity.
                    </p>
                </section>

                <div className="w-full h-px bg-gradient-to-r from-transparent via-tangerine/20 to-transparent my-16"></div>

                {/* The Problem */}
                <section className="mb-24 md:mb-32">
                    <h2 className="text-sm font-bold uppercase tracking-[0.3em] text-tangerine mb-12">The Problem</h2>
                    <div className="grid md:grid-cols-2 gap-12">
                        <div>
                            <h3 className="text-3xl font-bold mb-6 text-black">Expert knowledge is scarce.</h3>
                            <p className="text-gray-500 mb-6">
                                The world's most valuable insights are currently trapped within the cognitive limits of a few individuals. This creates a bottleneck that stifles innovation and slows global progress.
                            </p>
                        </div>
                        <div className="space-y-8">
                            <div className="border-l-2 border-tangerine/20 pl-6 py-2">
                                <h4 className="text-lg font-bold text-black mb-2">Time Constraints</h4>
                                <p className="text-sm text-gray-500">Experts are finite. There are only so many hours in a day for mentorship, consultation, and decision-making.</p>
                            </div>
                            <div className="border-l-2 border-tangerine/20 pl-6 py-2">
                                <h4 className="text-lg font-bold text-black mb-2">Geographic Barriers</h4>
                                <p className="text-sm text-gray-500">High-level knowledge is often concentrated in specific hubs, leaving underserved regions without the guidance they need.</p>
                            </div>
                            <div className="border-l-2 border-tangerine/20 pl-6 py-2">
                                <h4 className="text-lg font-bold text-black mb-2">Knowledge Attrition</h4>
                                <p className="text-sm text-gray-500">When experts move or retire, their specific logic and experience often disappear with them.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Our Approach */}
                <section className="mb-24 md:mb-32 p-8 md:p-12 bg-offwhite rounded-3xl border border-gray-200">
                    <h2 className="text-sm font-bold uppercase tracking-[0.3em] text-tangerine mb-12">Our Approach</h2>
                    <h3 className="text-3xl md:text-4xl font-bold mb-8 text-black">A New Model for Accessing Expertise</h3>
                    <div className="text-gray-600 space-y-6">
                        <p className="text-lg leading-relaxed">
                            Unlike generic LLMs trained on static web scrapes, Ally builds <span className="text-black font-semibold">Human-Grounded AI</span>. We partner directly with domain experts to capture their unique decision-making frameworks, nuances, and ethical considerations.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8">
                            <div>
                                <div className="text-tangerine font-bold text-xl mb-2">01.</div>
                                <h5 className="text-black font-bold mb-2">Expert Control</h5>
                                <p className="text-sm text-gray-500">Experts curate the data and verify the logic, ensuring the AI reflects their true standards.</p>
                            </div>
                            <div>
                                <div className="text-tangerine font-bold text-xl mb-2">02.</div>
                                <h5 className="text-black font-bold mb-2">Continuous Evolution</h5>
                                <p className="text-sm text-gray-500">The system learns and updates as the expert encounters new edge cases in the real world.</p>
                            </div>
                            <div>
                                <div className="text-tangerine font-bold text-xl mb-2">03.</div>
                                <h5 className="text-black font-bold mb-2">Trusted Scaling</h5>
                                <p className="text-sm text-gray-500">Reliable outputs that allow organizations to deploy expert-level logic at 1000x scale.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Technology */}
                <section className="mb-24 md:mb-32">
                    <h2 className="text-sm font-bold uppercase tracking-[0.3em] text-tangerine mb-12">Technology</h2>
                    <h3 className="text-3xl md:text-4xl font-bold mb-12 text-black">Infrastructure for Scalable Knowledge</h3>
                    <div className="space-y-12">
                        <div className="flex flex-col md:flex-row gap-8 items-start">
                            <div className="w-full md:w-1/3 text-tangerine font-mono text-xs pt-2 font-bold uppercase tracking-widest">Core Architecture</div>
                            <div className="w-full md:w-2/3">
                                <h4 className="text-2xl font-bold mb-4 text-black">Multi-modal RAG &amp; Translation</h4>
                                <p className="text-gray-500 mb-4">
                                    Our patent-pending translation technology processes multi-modal inputs—voice, vision, and text—into a structured knowledge graph that preserves context and intent better than standard vector search.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="px-3 py-1 bg-gray-100 border border-gray-200 rounded-full text-[10px] text-gray-500 font-bold uppercase">Patent-Pending</span>
                                    <span className="px-3 py-1 bg-gray-100 border border-gray-200 rounded-full text-[10px] text-gray-500 font-bold uppercase">Vision Models</span>
                                    <span className="px-3 py-1 bg-gray-100 border border-gray-200 rounded-full text-[10px] text-gray-500 font-bold uppercase">Voice Synthesis</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col md:flex-row gap-8 items-start">
                            <div className="w-full md:w-1/3 text-tangerine font-mono text-xs pt-2 font-bold uppercase tracking-widest">Security Protocol</div>
                            <div className="w-full md:w-2/3">
                                <h4 className="text-2xl font-bold mb-4 text-black">Secure and Owned</h4>
                                <p className="text-gray-500">
                                    Data privacy is paramount. Every instance is isolated, and the resulting AI models are owned by the creators. We provide the rails; the intelligence belongs to you.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Infrastructure */}
                <section className="mb-24 md:mb-32">
                    <div className="mb-16">
                        <h2 className="text-sm font-bold uppercase tracking-[0.3em] text-tangerine mb-4">Infrastructure</h2>
                        <h3 className="text-3xl md:text-4xl font-bold text-black">The Foundation of Scalable Intelligence</h3>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="p-8 bg-offwhite rounded-3xl border border-gray-200">
                            <div className="w-12 h-12 rounded-full bg-tangerine/10 flex items-center justify-center mb-6">
                                <span className="material-icons text-tangerine">layers</span>
                            </div>
                            <h4 className="text-xl font-bold mb-4 text-black">Multi-modal RAG</h4>
                            <p className="text-sm text-gray-500 leading-relaxed">
                                Dynamic retrieval augmented generation that processes voice, vision, and text simultaneously to build comprehensive context.
                            </p>
                        </div>
                        <div className="p-8 bg-offwhite rounded-3xl border border-gray-200">
                            <div className="w-12 h-12 rounded-full bg-tangerine/10 flex items-center justify-center mb-6">
                                <span className="material-icons text-tangerine">translate</span>
                            </div>
                            <h4 className="text-xl font-bold mb-4 text-black">Patent-Pending Translation</h4>
                            <p className="text-sm text-gray-500 leading-relaxed">
                                Proprietary technology that converts raw human input into structured, machine-interpretable knowledge graphs without loss of nuance.
                            </p>
                        </div>
                        <div className="p-8 bg-offwhite rounded-3xl border border-gray-200">
                            <div className="w-12 h-12 rounded-full bg-tangerine/10 flex items-center justify-center mb-6">
                                <span className="material-icons text-tangerine">shield</span>
                            </div>
                            <h4 className="text-xl font-bold mb-4 text-black">Secure Architecture</h4>
                            <p className="text-sm text-gray-500 leading-relaxed">
                                Enterprise-grade isolation ensuring data privacy. Your proprietary expertise remains yours, stored in encrypted, dedicated instances.
                            </p>
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-24 text-center border-t border-gray-200">
                    <h2 className="text-4xl md:text-5xl font-bold mb-10 text-black">Ready to scale your expertise?</h2>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <Link to="/contact" className="px-10 py-4 bg-tangerine hover:bg-tangerine/90 text-white shadow-md shadow-tangerine/20 font-bold rounded-xl transition-all transform hover:-translate-y-1 active:scale-95">
                            Contact Us
                        </Link>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <footer className="bg-offwhite py-16 border-t border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-10">
                    <div className="flex items-center gap-2 md:order-1">
                        <span className="material-icons text-tangerine text-2xl">change_history</span>
                        <span className="font-bold text-lg text-black">Ally AI</span>
                    </div>
                    <div className="flex space-x-10 md:order-2">
                        <a className="text-sm font-semibold text-gray-500 hover:text-tangerine transition-colors" href="#">Privacy</a>
                        <a className="text-sm font-semibold text-gray-500 hover:text-tangerine transition-colors" href="#">Terms</a>
                        <a className="text-sm font-bold text-tangerine hover:opacity-80 transition-colors" href="#">Contact</a>
                    </div>
                    <div className="flex space-x-6 md:order-3">
                        <a className="text-gray-400 hover:text-tangerine transition-colors" href="#"><i className="material-icons text-xl">facebook</i></a>
                        <a className="text-gray-400 hover:text-tangerine transition-colors flex items-center" href="#"><span className="font-bold text-lg leading-none">𝕏</span></a>
                        <a className="text-gray-400 hover:text-tangerine transition-colors" href="#"><i className="material-icons text-xl">photo_camera</i></a>
                    </div>
                    <div className="text-xs font-semibold text-gray-400 md:order-4">
                        © 2026 Ally AI Inc.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default AboutPage;
